import {
  FieldLabel,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import EmployeeIdTooltip from 'src/components/EmployeeIdTooltip/EmployeeIdTooltip'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { OrganizationRoles } from 'src/enums'
import { useFormFields } from 'src/hooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { UserListModel } from 'src/models/user.models'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import {
  PATCH_USER_EMPLOYEE_INIT,
  PatchUserActions,
} from 'src/redux/actions/data/users.actions'
import {
  selectIsCspAuthenticatedFromState,
  selectNullSafeLocaleFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { PatchUserRequest } from 'src/requests/patchUser.request'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { css } from 'styled-components'

interface EmployeeInfoBoxProps {
  className?: string
  user: UserListModel
  patchingUser: boolean
  patchingUserEmployee: boolean
  errorDetailMap?: ErrorDetailMap<string>
  patchUser: (
    body: Partial<PatchUserRequest>,
    patchType: PatchUserActions,
  ) => void
}

export type EmployeeInfoFlatFields = {
  employeeId?: string
}

const EmployeeInfoBox: React.FC<EmployeeInfoBoxProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false)
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const user = useSelector(selectUserListModelFromState)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const isGuestUser =
    showGuestRole && user?.organizationRole === OrganizationRoles.guest

  const {
    fields: flatFields,
    setFields: setFlatFields,
    fieldsErrors: flatFieldsErrors,
    onFieldChange: onFlatFieldChange,
    setErrorsAndReturnDidPassValidation: flatFieldsSetErrors,
  } = useFormFields<EmployeeInfoFlatFields>( // TODO: should make this string | undefined to be totally transparent
    {
      employeeId: props.user.employeeId,
    },
    () => ({}),
    props.errorDetailMap,
  )
  useEffect((): void => {
    setFlatFields({
      employeeId: props.user.employeeId,
    })
  }, [props.user])

  const onSuccessfulSubmit = (flatFields: EmployeeInfoFlatFields): void => {
    props.patchUser(
      {
        employeeId: flatFields.employeeId ? flatFields.employeeId : undefined,
        updateEmployeeId: true,
      },
      {
        type: PATCH_USER_EMPLOYEE_INIT,
      },
    )
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()

    const flatFieldsPassedValidation = flatFieldsSetErrors()

    if (flatFieldsPassedValidation) {
      onSuccessfulSubmit(flatFields)
    }
  }

  useEffect((): void => {
    if (props.patchingUser) {
      setHasSubmitted(false)
    }
  }, [props.patchingUser])

  useEffect((): void => {
    if (isEditing && !props.patchingUserEmployee) {
      setHasSubmitted(true)
      setIsEditing(false)
    }
  }, [props.patchingUserEmployee])

  useEffect((): void => {
    if (hasSubmitted) {
      setIsEditing(true)
    }
  }, [flatFieldsErrors])

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <span
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          ${getFontCss(
            20,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        <Trns source={'Employee Information'} />
        {user !== undefined && !isEditing && !isGuestUser && (
          <HoverableLink
            onClick={(): void => {
              setIsEditing(true)
            }}
            ariaLabelSource={'Update'}
            css={css`
              display: flex;
              align-items: center;
            `}
            colorSvgOnHover
            disabled={isCspAuthenticated}
          >
            <Trns source={'Update'} />
            <SvgUpdate
              className={'svg-update'}
              css={css`
                transform: scale(0.5);
              `}
            />
          </HoverableLink>
        )}
      </span>
      <span
        css={css`
          margin-top: 30px;
        `}
      >
        <Trns
          source={
            'To properly organize transactions, your company may need you to include some extra information.'
          }
        />
      </span>
      <form
        onSubmit={onSubmit}
        css={css`
          margin-top: 30px;
          .field-label {
            overflow: visible;
          }
        `}
      >
        <FieldLabel>
          <span
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            <Trns source={'Employee ID'} />
            <EmployeeIdTooltip
              openLeft={false}
              css={css`
                z-index: 1;
              `}
            />
          </span>
        </FieldLabel>
        {!isEditing && (
          <span>
            {props.user.employeeId !== undefined
              ? props.user.employeeId
              : '- -'}
          </span>
        )}
        {isEditing && (
          <span>
            <TextInputField
              design={TextInputFieldDesigns.New}
              errors={flatFieldsErrors['employeeId']}
              placeholder={translateSingleString(
                locale,
                'Enter ID your company provides',
              )}
              value={
                flatFields['employeeId'] !== undefined
                  ? flatFields['employeeId']
                  : ''
              }
              onChange={(val: string): void =>
                onFlatFieldChange('employeeId', val)
              }
            />
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 40px;
                margin-top: 40px;
              `}
            >
              <Button
                design={ButtonDesigns.designCta}
                type={ButtonTypes.submit}
                text={<Trns source={'Save Employee Info'} />}
                loading={props.patchingUserEmployee}
                disabled={props.patchingUser}
                big
              />
              <HoverableLink
                onClick={(): void => {
                  setIsEditing(false)
                }}
                underline
                ariaLabelSource={'Cancel'}
              >
                <Trns source={'Cancel'} />
              </HoverableLink>
            </div>
          </span>
        )}
      </form>
    </Box>
  )
}

export default EmployeeInfoBox
